import { useParams, Link, useNavigate } from "react-router-dom";

import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Link2,
  Globe,
  ExternalLink,
  GraduationCap,
  Building2,
  Scroll,
  Bookmark,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Section, Badge, AnimatedCard } from "@/components/ui/shared";
import { useBookmarks } from "@/lib/bookmarks";
import { useToast } from "@/components/ui/Toast";
import ReadingProgress from "@/components/ui/ReadingProgress";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import { articleJsonLd, snippet } from "@/lib/seo";
import JsonLd from "@/components/ui/JsonLd";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import { errorMessage, isNotFound, useWorldEvent, usePageView } from "@/lib/api";
import type { AcademicSourceResponse as AcademicSource, SeerahCrossReference } from "@/lib/api";

/* ============================================
   Academic Source Card
   ============================================ */

function AcademicSourceCard({ source, index }: { source: AcademicSource; index: number }) {
  return (
    <AnimatedCard delay={index * 0.08}>
      <div className="card-elevated p-5 hover:border-[var(--color-gold)]/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <GraduationCap className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge value={source.sourceType.replace("academic_", "")} />
              {source.yearPublished && (
                <span className="text-[10px] text-[var(--color-ink-muted)]">{source.yearPublished}</span>
              )}
            </div>
            <p className="text-sm font-semibold text-[var(--color-midnight)]">{source.author}</p>
            <p className="text-sm text-[var(--color-ink)] italic">{source.title}</p>
            <p className="text-xs text-[var(--color-ink-muted)] mt-0.5">
              {source.publication}{source.pageReference ? ` · ${source.pageReference}` : ""}
            </p>
            {source.excerpt && (
              <div className="mt-3 p-3 rounded-lg bg-[var(--color-cream)]/50 border border-[var(--color-cream-dark)]">
                <p className="text-xs text-[var(--color-ink-muted)] leading-relaxed italic">"{source.excerpt}"</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AnimatedCard>
  );
}

/* ============================================
   World History Detail Page
   ============================================ */

export default function TimelineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  // API hook
  const { data: event, isLoading, isError, error, refetch } = useWorldEvent(id);

  // Normalize optional arrays/strings to avoid runtime/TS errors
  const description = event?.description ?? "";
  const sources: AcademicSource[] = event?.sources ?? [];
  const seerah: SeerahCrossReference[] = event?.seerahCrossReferences ?? [];

  const bookmarked = id ? isBookmarked(`timeline-${id}`) : false;

  useSEO(event ? event.title : "World History Event", snippet(event?.description), { type: "article", noindex: !isLoading && !event });

  usePageView("world_history", { id: event?.id, type: "event", title: event?.title });

  const handleBookmark = () => {
    if (!event || !id) return;
    const added = toggle({
      id: `timeline-${id}`,
      type: "timeline",
      title: event.title,
      subtitle: `${event.civilization} · ${event.dateDisplay}`,
      route: `/timeline/${id}`,
    });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  // Loading
  if (isLoading) {
    return (
      <main className="pt-16">
        <div className="bg-[var(--color-midnight)] py-16 md:py-24" />
        <Section><SkeletonDetailPage /></Section>
      </main>
    );
  }

  // Error (a 404 falls through to "not found")
  if (isError && !isNotFound(error)) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Failed to Load Event</h2>
            <p className="text-[var(--color-ink-muted)] mb-6">{errorMessage(error)}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => void refetch()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-[var(--color-cream)]">
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
              <Link to="/timeline" className="btn-gold inline-flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Timeline
              </Link>
            </div>
          </div>
        </Section>
      </main>
    );
  }

  // Not found
  if (!event) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <Globe className="w-16 h-16 text-[var(--color-cream-dark)] mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Event Not Found</h2>
            <p className="text-[var(--color-ink-muted)] mb-6">This event doesn't exist or has been removed.</p>
            <Link to="/timeline" className="btn-gold inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Timeline
            </Link>
          </div>
        </Section>
      </main>
    );
  }

  return (
    <main className="pt-16">
      <ReadingProgress />
      <JsonLd data={articleJsonLd({ title: event.title, description: snippet(event.description), path: `/timeline/${event.id}`, datePublished: event.createdAt, section: "World History" })} />
      {/* Hero */}
      <div className="bg-[var(--color-midnight)] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pattern-star" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-midnight)] via-transparent to-[var(--color-midnight)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigate("/timeline")} className="inline-flex items-center gap-2 text-[var(--color-cream)]/50 hover:text-[var(--color-cream)] text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to World Timeline
              </button>
              <button onClick={handleBookmark}
                className={`p-2 rounded-lg transition-colors ${bookmarked ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10" : "text-[var(--color-cream)]/40 hover:text-[var(--color-gold)] hover:bg-white/5"}`}>
                <Bookmark className={`w-5 h-5 ${bookmarked ? "fill-current" : ""}`} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge value={event.civilization} />
              <Badge value={event.category} />
              <span className="text-xs text-[var(--color-cream)]/40 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {event.dateDisplay}
              </span>
            </div>

            <h1 className="font-[var(--font-display)] text-3xl md:text-5xl font-bold text-[var(--color-cream)] leading-tight">
              {event.title}
            </h1>

            {event.location && (
              <p className="text-[var(--color-cream)]/40 text-sm mt-4 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {event.location}
                {event.region && <span className="text-[var(--color-cream)]/20">· {event.region}</span>}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      <Section>
        <div className="max-w-4xl mx-auto">
          <Breadcrumbs crumbs={[{ label: "Timeline", to: "/timeline" }, { label: event.title }]} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <AnimatedCard>
                <div>
                  {description.split("\n\n").map((para: string, i: number) => (
                    <p key={i} className="text-[var(--color-ink)] leading-relaxed mb-4">{para}</p>
                  ))}
                </div>
              </AnimatedCard>

              {/* Significance */}
              {event.significance && (
                <AnimatedCard delay={0.1}>
                  <div className="bg-gradient-to-r from-[var(--color-navy)]/5 to-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-[var(--font-display)] text-lg font-semibold text-[var(--color-midnight)] mb-2 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[var(--color-navy)]" /> Historical Significance
                    </h3>
                    <p className="text-sm text-[var(--color-ink)] leading-relaxed">{event.significance}</p>
                  </div>
                </AnimatedCard>
              )}

              {/* Islamic Relevance */}
              {event.islamicRelevance && (
                <AnimatedCard delay={0.15}>
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
                    <h3 className="font-[var(--font-display)] text-lg font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <Scroll className="w-5 h-5" /> Islamic Relevance
                    </h3>
                    <p className="text-sm text-emerald-900/80 leading-relaxed">{event.islamicRelevance}</p>
                  </div>
                </AnimatedCard>
              )}

              {/* Academic Sources */}
              {sources.length > 0 && (
                <div>
                  <h3 className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-midnight)] mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-amber-600" /> Academic Sources
                    <span className="text-xs text-[var(--color-ink-muted)] font-normal ml-1">(from non-Muslim historians)</span>
                  </h3>
                  <div className="space-y-3">
                    {sources.map((source: AcademicSource, i: number) => (
                      <AcademicSourceCard key={source.id} source={source} index={i} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Seerah Cross-References */}
              {seerah.length > 0 && (
                <AnimatedCard delay={0.2}>
                  <div className="card-elevated p-5">
                    <h4 className="font-[var(--font-display)] text-base font-semibold text-[var(--color-midnight)] mb-4 flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-emerald-600" /> Seerah Connections
                    </h4>
                    <div className="space-y-3">
                      {seerah.map((ref: SeerahCrossReference, i: number) => (
                        <Link key={i} to={`/seerah/${ref.seerahEventId}`}
                          className="block p-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 transition-all group">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge value={ref.linkType} />
                          </div>
                          <p className="text-sm font-semibold text-emerald-800 group-hover:text-emerald-900">
                            {ref.seerahTitle}
                          </p>
                          {ref.description && (
                            <p className="text-xs text-emerald-700/70 mt-1 leading-relaxed">{ref.description}</p>
                          )}
                        </Link>
                      ))}
                    </div>
                    <Link to="/seerah" className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 mt-3 font-medium transition-colors">
                      View all Seerah events <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </AnimatedCard>
              )}

              {/* Quick Facts */}
              <AnimatedCard delay={0.3}>
                <div className="card-elevated p-5">
                  <h4 className="font-[var(--font-display)] text-base font-semibold text-[var(--color-midnight)] mb-4">Quick Facts</h4>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Dates (CE)</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{event.dateDisplay}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Civilization</dt>
                      <dd className="text-[var(--color-midnight)] font-medium capitalize">{String(event.civilization).replace(/_/g, " ")}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Region</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{event.region || event.location}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Academic Sources</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{sources.length} references</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Seerah Links</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{seerah.length || 0} cross-references</dd>
                    </div>
                  </dl>
                </div>
              </AnimatedCard>
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}

